import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { getAccessToken, getServiceId } from '@/api/tokens.helper';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import { useAuth } from '@/store/auth/auth.store';
import { useCurrentServiceProvider } from '@/store/service-providers/current-service-provider.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  deleteNotifications,
  getNotification,
  getNotifications,
  readAllNotifications,
  readNotifications,
  unreadNotifications,
} from './notifications.api';
import {
  type DeleteNotificationsDto,
  type InviteCanceledData,
  type NotificationsListFilters,
  NotificationType,
  type ReadNotificationsDto,
} from './notifications.dto';
import { mapWsNotificationEvent } from './notifications.mappers';

const NOTIFICATIONS_KEY = 'notifications';

/** Whether a pushed event says this person was just taken off the service the app is on. */
const removedFromActiveService = (raw: unknown): boolean => {
  try {
    const event = JSON.parse(String(raw)) as { type?: string; data?: { service_id?: unknown } };
    return (
      event.type === NotificationType.ROLES_REMOVED && event.data?.service_id === getServiceId()
    );
  } catch {
    return false;
  }
};

/**
 * Everything asked of a service its member has left is refused, the notifications telling them so
 * included. The session is reloaded, which settles on an account they still have (their own space
 * when none is left), and the account and vessel state are dropped for Layout to resolve again.
 */
const leaveRemovedService = async () => {
  try {
    await useAuth.getState().me();
  } catch (error) {
    console.error('Failed to reload the session after leaving a service:', error);
    return;
  }

  useActiveAccount.setState({ serviceId: undefined, serviceType: undefined });
  useVessels.setState({ myVessels: undefined, vessel: undefined });
};

export const useNotificationsQuery = (
  limit: number,
  filters?: NotificationsListFilters,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: [NOTIFICATIONS_KEY, limit, filters],
    queryFn: ({ pageParam }) => getNotifications(limit, pageParam, filters),
    initialPageParam: 1,
    getNextPageParam: getInfiniteQueryListNextParam,
    enabled,
  });

export const useNotificationQuery = (id: string) =>
  useQuery({
    queryKey: [NOTIFICATIONS_KEY, id],
    queryFn: () => getNotification(id),
    enabled: !!id,
  });

export const useReadNotificationsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ReadNotificationsDto) => readNotifications(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
};

export const useUnreadNotificationsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: ReadNotificationsDto) => unreadNotifications(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
};

export const useReadAllNotificationsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => readAllNotifications(),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
};

export const useDeleteNotificationsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: DeleteNotificationsDto) => deleteNotifications(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
    },
  });
};

const WS_URL = import.meta.env.VITE_WS_NOTIFICATION;

export const useNotificationsWs = () => {
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();

  const { data: notifications } = useNotificationsQuery(1);

  const unreadCount = notifications?.pages?.[0].unread ?? 0;

  const vesselId = useVessels((s) => s.vessel?.id);
  const spId = useCurrentServiceProvider((s) => s.selectedServiceProviderId);
  const serviceId = vesselId ?? spId;

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const params = new URLSearchParams({ jwt: token });
    if (serviceId) params.append('service_id', serviceId);

    const ws = new WebSocket(`${WS_URL}?${params.toString()}`);
    wsRef.current = ws;

    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    ws.onmessage = (message: MessageEvent) => {
      if (removedFromActiveService(message.data)) {
        void leaveRemovedService().then(() =>
          queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
        );
        return;
      }

      // A withdrawn invitation must lose its Accept/Decline at once, not after the debounce.
      const event = mapWsNotificationEvent(message.data);
      if (event?.type === NotificationType.INVITE_CANCELED) {
        const { id } = event.data as InviteCanceledData;
        queryClient.invalidateQueries({ queryKey: ['invites', id] });
        queryClient.invalidateQueries({ queryKey: ['invites-list'] });
      }

      if (invalidateTimer) clearTimeout(invalidateTimer);
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null;
        queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] });
      }, 2000);
    };

    return () => {
      if (invalidateTimer) clearTimeout(invalidateTimer);
      ws.close();
    };
  }, [queryClient, serviceId]);

  return { unreadCount };
};
